export type InteractionTab = 'home' | 'search' | 'cookbook' | 'orders';

export type InteractionContainer = 'carousel';

export type InteractionContentType =
  | 'standard_photo'
  | 'editorial_photo'
  | 'gif'
  | 'video'
  | 'context_photo';

/**
 * Pool a feed item was drawn from, in snake_case.
 *
 * The consolidated home feed stamps this server-side as `content_source`, and
 * the backend can add a source without a client release — so this is a widened
 * string rather than a closed union. `KnownRecommendationSource` documents the
 * values in use today and gives editors autocomplete without rejecting new ones.
 */
export type KnownRecommendationSource = 'marketing_list' | 'sc_ranked';

export type RecommendationSource =
  | KnownRecommendationSource
  | (string & NonNullable<unknown>);

export interface FeedInteractionFields {
  /** Tab where the interaction occurred. */
  tab?: InteractionTab;
  /**
   * Session-scoped UUID grouping all interaction events that share one mobile
   * home tab session. Stable for the lifetime of the tab session
   * (sessionStorage-backed).
   */
  tabSessionId?: string;
  /** `"carousel"` when inside a marketing/editorial carousel; `null` for feed rows. */
  container?: InteractionContainer | null;
  /** Row position in a ranked list. */
  rankedListPosition?: number;
  /** 0-indexed row position in a vertical feed. */
  verticalPosition?: number;
  /** 0-indexed position within a carousel row; `null` when not in a carousel. */
  horizontalPosition?: number | null;
  contentType?: InteractionContentType;
  recommendationSource?: RecommendationSource | null;
  carouselId?: number | null;
  carouselName?: string | null;
  /** Links subsequent actions to the originating viewed event. */
  impressionId?: string | null;
  cookbookOwnerId?: string | null;
}

export function buildFeedInteractionPayload({
  tab,
  tabSessionId,
  container,
  rankedListPosition,
  verticalPosition,
  horizontalPosition,
  contentType,
  recommendationSource,
  carouselId,
  carouselName,
  impressionId,
  cookbookOwnerId,
}: FeedInteractionFields) {
  return {
    ...(tab !== undefined && { tab }),
    ...(tabSessionId !== undefined && { tab_session_id: tabSessionId }),
    ...(container !== undefined && { container }),
    ...(rankedListPosition !== undefined && {
      ranked_list_position: rankedListPosition,
    }),
    ...(verticalPosition !== undefined && {
      vertical_position: verticalPosition,
    }),
    ...(horizontalPosition !== undefined && {
      horizontal_position: horizontalPosition,
    }),
    ...(contentType !== undefined && { content_type: contentType }),
    ...(recommendationSource !== undefined && {
      recommendation_source: recommendationSource,
    }),
    ...(carouselId !== undefined && { carousel_id: carouselId }),
    ...(carouselName !== undefined && { carousel_name: carouselName }),
    ...(impressionId !== undefined && { impression_id: impressionId }),
    ...(cookbookOwnerId !== undefined && {
      cookbook_owner_id: cookbookOwnerId,
    }),
  };
}
